export type Me = {
  id: string;
  email: string;
  role: "client" | "admin";
  locale: "zh-Hant" | "zh-Hans" | "en";
  display_name?: string | null;
  referral_code?: string | null;
  avatar_id?: string | null;
  tick_flash_enabled?: boolean;
  tick_flash_seconds?: number;
  idle_timeout_seconds?: number;
  product_plan?: {
    slug: string;
    name_zh_hant: string;
    name_en: string;
    demo_connection_quota: number;
    live_connection_quota: number;
    connection_quota: number;
    assigned?: boolean;
  } | null;
  demo_connection_quota?: number;
  live_connection_quota?: number;
  connection_quota?: number;
  quota?: {
    demo: { used: number; limit: number };
    live: { used: number; limit: number };
    used?: number;
    limit?: number;
  };
  google_linked?: boolean;
  telegram_linked?: boolean;
  has_password?: boolean;
  copy_offer_enabled?: boolean;
};

function formatDetail(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) return String((item as { msg: string }).msg);
        return JSON.stringify(item);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    const obj = detail as { message?: unknown; code?: unknown; msg?: unknown };
    if (typeof obj.message === "string" && obj.message.trim()) return obj.message;
    if (typeof obj.msg === "string" && obj.msg.trim()) return obj.msg;
    if (typeof obj.code === "string" && obj.code.trim()) return obj.code;
  }
  if (detail != null) return JSON.stringify(detail);
  return "";
}

/** True when the engine rejected bind/approve for connection quota. */
export function isQuotaExhaustedError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /quota_exhausted|配額已用完|配額已滿|quota (reached|exhausted)/i.test(msg);
}

/** True when the engine rejected bind / strategy / copy because the venue is closed. */
export function isVenueClosedError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /venue_closed|venue_hard_stop|venue_unavailable|broker_closed|broker_hard_stop|此場地已關閉|此場地已硬停|此場地尚未|此經紀商已關閉|此經紀商已硬停|not accepting new binds|hard-stopped|not available/i.test(
    msg,
  );
}

let authRedirecting = false;

function redirectIfSessionDead(res: Response, detail: string) {
  if (typeof window === "undefined" || authRedirecting || res.status !== 401) return;
  const path = window.location.pathname;
  if (!path.startsWith("/dashboard") && !path.startsWith("/admin")) return;
  const kicked =
    res.headers.get("x-kairos-auth") === "session_revoked" ||
    /Session revoked|登入已失效/.test(detail);
  authRedirecting = true;
  window.location.replace(kicked ? "/login?kicked=1" : "/login");
}

async function parse(res: Response) {
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { detail: text };
  }
  if (!res.ok) {
    const detail = formatDetail(data) || res.statusText;
    redirectIfSessionDead(res, detail);
    throw new Error(detail);
  }
  return data;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(init?.headers || {}),
    },
  });
  return parse(res) as Promise<T>;
}
